import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/axios";

// -------------------- Async Thunks --------------------

// Fetch all point exchange rates
export const fetchPointExchangeRates = createAsyncThunk(
  "pointExchangeRate/fetchPointExchangeRates",
  async (companyId = null, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/point-exchange-rates", {
        params: companyId ? { company_id: companyId } : {},
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || {
          message: "Failed to fetch point exchange rates",
        }
      );
    }
  }
);

// Add a new point exchange rate
export const addPointExchangeRate = createAsyncThunk(
  "pointExchangeRate/addPointExchangeRate",
  async (data, { rejectWithValue }) => {
    try {
      const res = await api.post("/api/point-exchange-rates", data);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || { message: "Failed to add point exchange rate" }
      );
    }
  }
);

// Fetch current active rate
export const fetchCurrentRate = createAsyncThunk(
  "pointExchangeRate/fetchCurrentRate",
  async (companyId = null, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/point-exchange-rates/current", {
        params: companyId ? { company_id: companyId } : {},
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || { message: "Failed to fetch current rate" }
      );
    }
  }
);

// -------------------- Slice --------------------

const pointExchangeRateSlice = createSlice({
  name: "pointExchangeRate",
  initialState: {
    list: [],
    currentRate: null,
    status: "idle",
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch List
      .addCase(fetchPointExchangeRates.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchPointExchangeRates.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.list = action.payload;
      })
      .addCase(fetchPointExchangeRates.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || action.error.message;
      })

      // Add
      .addCase(addPointExchangeRate.pending, (state) => {
        state.status = "loading";
      })
      .addCase(addPointExchangeRate.fulfilled, (state, action) => {
        state.status = "succeeded";
        // Since we might be deleting a collision on the backend, 
        // it's safer to just refetch the whole list or handle it carefully.
        // For simplicity, we'll add it to the list and sort.
        if (!Array.isArray(state.list)) state.list = [];
        
        // Remove collision if any (exact match on effective_from)
        state.list = state.list.filter(
          (item) =>
            item.effective_from !== action.payload.effective_from ||
            (item.company_id ?? null) !== (action.payload.company_id ?? null)
        );
        
        state.list.unshift(action.payload);
        state.list.sort((a, b) => new Date(b.effective_from) - new Date(a.effective_from));
      })
      .addCase(addPointExchangeRate.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || action.error.message;
      })

      // Fetch Current
      .addCase(fetchCurrentRate.fulfilled, (state, action) => {
        state.currentRate = action.payload;
      });
  },
});

export const { clearError } = pointExchangeRateSlice.actions;
export default pointExchangeRateSlice.reducer;
